import React from 'react'
import styled from 'styled-components'
import {injectIntl} from 'react-intl'
import {Delete, Create, Done, Close, SupervisorAccount} from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import UserImage from 'components/UserImage'
import MultiSelect from 'components/MultiSelect'
import Confirmation from 'components/Confirmation'
import Dialog from 'components/Dialog'
import permissions from 'utils/enums/permissions'

const Container = styled.div`
    box-sizing: border-box;
    align-items: center;
    padding: 20px;
    display: flex;
    width: 100%;
    background: #FFF;
    z-index: ${props => props.editable && 1400};
`

const Option = styled.div`
    pointer-events: ${props => !props.editable && 'none'};
    text-transform: uppercase;
    padding: 4px 10px;
    font-size: 10px;
    background-color: ${props => props.active ? '#415dd4' : 'rgba(160, 183, 190, .24)'};
    color: ${props => props.active ? '#fff' : '#7c9198'};
    cursor: pointer;
    margin-top: 5px;
    display: inline-block;
    border-radius: 2px;
    letter-spacing: 1px;

    :not(:last-child) {
        margin-right: 5px;
    }
`

const Block = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 20px;
`

const Name = styled.div`
    font-size: 20px;
`

const Row = styled.div`
    display: flex;
    padding: 2px 0 4px;
    flex-wrap: wrap;

`

const Title = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.54);
    margin-right: 10px;
    margin-top: 20px;
    margin-bottom: 5px;
`

const Chip = styled.div`
    border-radius: 15px;
    font-size: 12px;
    color: #4a4a4a;
    background: rgb(230, 230, 230);
    margin: 2px 5px 2px 2px;
    padding: 2.5px 6px;
`

const UserSettings = ({user, edit, error, entities, modules, handleDelete,
    handleChange, handleSave, handleClose, handleEdit, handleSet, intl}) => {

    const [open, setOpen] = React.useState(null)

    const openDialog = (type) => {
        setOpen(type)
    }

    const closeDialog = () => {
        setOpen(null)
    }

    const handleConfirm = () => {
		if (open === 'delete') {
			handleDelete(user.name)
		} else {
			handleSet(user.id)
		}
        
        closeDialog()
    }

    const handleRename = (e) => {
        handleChange(e.target.value, 'name')
    }

    const handleOptionClick = (permission) => {
        const options = {...userPermissions}
        options[permission] = !options[permission]

        if (permissions[permission] === permissions.Admin && options[permission]) {
            options.Designer = true
            options.Restricted = false
            options.Read = true
            options.Write = true
            options.Delete = true
        }

        if (permissions[permission] === permissions.Designer && !options[permission]) {
            options.Admin = false
        }

        if (permissions[permission] === permissions.Restricted && options[permission]) {
            options.Admin = false
            options.Designer = false
        }

        if (permissions[permission] === permissions.Designer && options[permission]) {
            options.Restricted = false
            options.Read = true
            options.Write = true
            options.Delete = true
        }

        if (permissions[permission] === permissions.Read && !options[permission]) {
            options.Designer = false
            options.Admin = false
        }

        if (permissions[permission] === permissions.Write && !options[permission]) {
            options.Designer = false
            options.Admin = false
        }

        if (permissions[permission] === permissions.Delete && !options[permission]) {
            options.Designer = false
            options.Admin = false
        }

        const values = Object.keys(options).filter(item => options[item])
        const sum = values.map(item =>
            permissions[item]).reduce((previousValue, currentValue) => {
                return previousValue | currentValue
            }, 0)

        handleChange(sum, 'permissions')
    }

    const handleSelect = (value, key) => {
        const data = value ? value.map(item => item.value) : []
        handleChange(data, key)
    }

    const getPermissions = () => {
        const obj = {}

        Object.keys(permissions).forEach(key => {
            obj[key] = (user.permissions & permissions[key]) === permissions[key]
        })

        return obj
    }

    const userPermissions = getPermissions()

    return (
        <Container data-user={user.id} editable={edit}>
            <UserImage
                value={user.id ? user.logo : 'New'}
                size='large'
                style={!user.id ? {fontSize: '25px', color: '#FFF', backgroundColor: '#76bd7d'} : {}}
            />
            <Block>
                {user.id ?
                    <Name>{user.name}</Name> :
                    <TextField variant="standard"
                        error={error}
                        value={user.name}
                        onChange={handleRename}
                        label={error ?
                            intl.formatMessage({id: "settings.user_exist"}) :
                            intl.formatMessage({id: "settings.enter_name"})}
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            name: 'name'
                        }}
                    />
                }
                <div>
                    {Object.keys(permissions).map(item =>
                        <Option key={item}
                            active={userPermissions[item]}
                            onClick={permissions[item] !== permissions.Read ?
                                () => handleOptionClick(item) : undefined}
                            editable={edit}
                        >
                            {intl.formatMessage({id: `settings.${item.toLowerCase()}`})}
                        </Option>
                    )}
                </div>
                <React.Fragment>
                    {(user.permissions & permissions.Designer) !== permissions.Designer &&
                        <React.Fragment>
                            {edit ?
                                <React.Fragment>
                                    <Title>
                                        {intl.formatMessage({id: "settings.disabled_tables"})}
                                    </Title>
                                    <MultiSelect
                                        defaultValue={user.disabledTables.length ?
                                            user.disabledTables.map(item => {
                                                const entity = entities.find(entity => entity.id === item)

                                                return entity && {
                                                    value: item,
                                                    label: entity.singular
                                                }
                                            }) : null
                                        }
                                        options={entities.map(item => { return {value: item.id, label: item.singular} })}
                                        onChange={(value) => handleSelect(value, 'table')}
                                        maxHeight="initial"
                                    />
                                    <Title>
                                            {intl.formatMessage({id: "settings.disabled_modules"})}
                                    </Title>
                                    <MultiSelect
                                        defaultValue={user.disabledModules.length ?
                                            user.disabledModules.map(item => {
                                                const obj = modules.find(module => module.id === item)

                                                return obj && {
                                                    value: item,
                                                    label: obj.name
                                                }
                                            }) : null
                                        }
                                        options={modules.map(item => { return {value: item.id, label: item.name} })}
                                        onChange={(value) => handleSelect(value, 'module')}
                                        maxHeight="initial"
                                    />
                                </React.Fragment>
                                :
                                <React.Fragment>
                                    {!!user.disabledTables.length &&
                                        <React.Fragment>
                                            <Title>
                                                {intl.formatMessage({id: "settings.disabled_tables"})}
                                            </Title>
                                            <Row>
                                                {user.disabledTables.map(item => {
                                                    const entity = entities.find(entity => entity.id === item)

                                                    return entity && <Chip key={item}>
                                                        {entity.singular}
                                                    </Chip>
                                                })}
                                            </Row>
                                        </React.Fragment>
                                    }
                                    {!!user.disabledModules.length &&
                                        <React.Fragment>
                                            <Title>
                                                {intl.formatMessage({id: "settings.disabled_modules"})}
                                            </Title>
                                            <Row>
                                                {user.disabledModules.map(item => {
                                                    const obj = modules.find(module => module.id === item)

                                                    return obj && <Chip key={item}>
                                                        {obj.name}
                                                    </Chip>
                                                })}
                                            </Row>
                                        </React.Fragment>
                                    }
                                </React.Fragment>
                            }
                        </React.Fragment>
                    }
                </React.Fragment>
            </Block>
            {edit ?
                <React.Fragment>
                    <IconButton disabled={!user.name.replace(/\s/g, '') || error}
                        onClick={handleSave}
						style={{ marginLeft: 'auto' }}
                    >
                        <Done />
                    </IconButton>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </React.Fragment>
                :
                <React.Fragment>
					<Tooltip placement="top"
						title={intl.formatMessage({id: user.isOwner ? "settings.user_owner" : "settings.set_owner"})}
					>
						<div style={{ marginLeft: 'auto' }}>
							<IconButton disabled={user.isOwner} onClick={() => openDialog('set')}>
								<SupervisorAccount />
							</IconButton>
						</div>
					</Tooltip>
					<Tooltip
						placement="top"
						title={intl.formatMessage({id: "settings.edit_user"})}
					>
						<IconButton onClick={() => handleEdit(user.id)}>
							<Create />
						</IconButton>
					</Tooltip>
					<Tooltip
						placement="top"
						title={intl.formatMessage({
							id: user.isOwner ? "settings.owner_not_detach" : "settings.detach"})}
					>
						<div>
							{user.isOwner ?
								<IconButton disabled>
									<Delete />
								</IconButton> :
								<IconButton onClick={() => openDialog('delete')} color='secondary'>
									<Delete />
								</IconButton>
							}
						</div>
					</Tooltip>
                </React.Fragment>
            }
            {open &&
                <Dialog
                    open={!!open}
                    onClose={closeDialog}
                >
                    {open === 'delete' ?
						<Confirmation
							text={intl.formatMessage({id: "settings.confirm_detach"}, {
								name: user.name})}
							button={intl.formatMessage({id: "app.confirm"})}
							title={intl.formatMessage({id: "app.confirm_action"})}
							handleClose={closeDialog}
							handleConfirm={handleConfirm}
						/>
						:
						<Confirmation
							text={intl.formatMessage({id: "settings.confirm_set_owner"}, {
								name: user.name})}
							button={intl.formatMessage({id: "app.confirm"})}
							title={intl.formatMessage({id: "app.confirm_action"})}
							handleClose={closeDialog}
							handleConfirm={handleConfirm}
						/>
					}
                </Dialog>
            }
        </Container>
    )
}

export default injectIntl(UserSettings)
